import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '../../../../supabaseClient';
import { sendExternalAction } from '../../../../core/utils/externalLinks';
import SignaturePad from '../SignaturePad';
import './DigitalSignature.scss';

const DigitalSignature = ({
  values,
  localSignatureData,
  setLocalSignatureData,
  isSignatureReset,
  setIsSignatureReset,
  checkboxLabel = 'Eu autorizo',
  onSaveBeforeSignature,
}) => {
  const location = useLocation();
  const isExternalRoute = location.pathname.includes('/external-os/');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const signaturePadRef = useRef(null);

  async function handleConfirmSignature() {
    if (!signaturePadRef.current) return;

    try {
      setIsSaving(true);

      // Determina o ID da OS a ser usado
      let osId = values?.id;

      // Se não existe ID da OS (é nova), precisa salvar primeiro
      if (!osId && onSaveBeforeSignature) {
        const savedId = await onSaveBeforeSignature();
        if (!savedId) {
          Swal.fire({
            title: 'Erro',
            text: 'É necessário salvar a ordem de serviço antes de assinar.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
          setIsSaving(false);
          return;
        }
        osId = savedId;
      }

      // Pega a assinatura em Base64
      const signatureBase64 = signaturePadRef.current.getSignatureData();

      // Cria a data atual no timezone local (Brasil)
      const now = new Date();
      const localDate = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      );
      const signatureDate = localDate
        .toISOString()
        .slice(0, 19)
        .replace('T', ' ');

      // Externo (link público) grava via Edge Function; interno grava direto.
      let error;
      if (isExternalRoute) {
        const token = location.pathname.split('/')[2];
        const res = await sendExternalAction('os', token, 'sign', signatureBase64);
        error = res.error;
      } else {
        const res = await supabase
          .from('BudgetServiceOrder')
          .update({
            client_authorization: true,
            client_signature: signatureBase64,
            signature_date: signatureDate,
          })
          .eq('id', osId);
        error = res.error;
      }

      if (error) {
        console.error('Erro ao salvar assinatura:', error);
        Swal.fire({
          title: 'Erro ao salvar assinatura',
          text: 'Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      // Atualiza o estado local com a assinatura
      setLocalSignatureData({
        signature: signatureBase64,
        date: signatureDate,
        authorized: true,
      });

      // Reseta o flag de reset caso estivesse ativo
      setIsSignatureReset(false);

      // Fecha o modal e mantém o checkbox marcado
      setShowSignatureModal(false);

      Swal.fire({
        title: 'Sucesso!',
        text: 'Assinatura salva com sucesso!',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error('Erro ao processar assinatura:', error);
      Swal.fire({
        title: 'Erro ao processar assinatura',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleResetSignature() {
    const confirmation = await Swal.fire({
      title: 'Resetar Assinatura?',
      text: 'Esta ação é apenas para testes em homologação.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, resetar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirmation.isConfirmed) return;

    try {
      const { error } = await supabase
        .from('BudgetServiceOrder')
        .update({
          client_authorization: false,
          client_signature: null,
          signature_date: null,
        })
        .eq('id', values.id);

      if (error) {
        console.error('Erro ao resetar assinatura:', error);
        Swal.fire({
          title: 'Erro ao resetar',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      // Limpa o estado local e marca como resetado
      setLocalSignatureData(null);
      setIsAuthorized(false);
      setIsSignatureReset(true);

      Swal.fire({
        title: 'Assinatura resetada!',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error('Erro ao resetar:', error);
    }
  }

  const hasSignature =
    (values?.client_signature && !isSignatureReset) || localSignatureData;
  const isCheckboxDisabled = hasSignature;

  return (
    <div className="digital-signature">
      {/* Checkbox de Autorização */}
      <label
        className={`digital-signature__checkbox-label ${
          hasSignature
            ? 'digital-signature__checkbox-label--disabled'
            : 'digital-signature__checkbox-label--enabled'
        }`}
      >
        <input
          type="checkbox"
          checked={
            !isSignatureReset &&
            (values?.client_authorization ||
              localSignatureData?.authorized ||
              isAuthorized)
          }
          onChange={(e) => {
            if (isCheckboxDisabled) return;
            setIsAuthorized(e.target.checked);
            if (e.target.checked) {
              setIsSignatureReset(false);
              setShowSignatureModal(true);
            }
          }}
          disabled={isCheckboxDisabled}
          className={`digital-signature__checkbox ${
            hasSignature
              ? 'digital-signature__checkbox--disabled'
              : 'digital-signature__checkbox--enabled'
          }`}
        />
        {checkboxLabel}
      </label>

      {/* Exibir assinatura existente logo abaixo do checkbox */}
      {hasSignature && (
        <div className="digital-signature__signature-container">
          <div className="digital-signature__signature-card">
            <h5 className="digital-signature__signature-title">
              ✓ Assinatura Digital
            </h5>
            <img
              src={localSignatureData?.signature || values.client_signature}
              alt="Assinatura do cliente"
              className="digital-signature__signature-image"
            />
            <div className="digital-signature__signature-date">
              Assinado em:{' '}
              {localSignatureData?.date
                ? new Date(localSignatureData.date).toLocaleString('pt-BR')
                : values?.signature_date
                ? new Date(values.signature_date).toLocaleString('pt-BR')
                : 'Data não disponível'}
            </div>

            {!isExternalRoute && (
              <button
                onClick={handleResetSignature}
                className="digital-signature__reset-button"
                title="Apenas para testes em homologação"
              >
                🔄 Resetar
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal de Assinatura em Tela Cheia */}
      {showSignatureModal && (
        <div className="signature-modal">
          <div className="signature-modal__header">
            <h3 className="signature-modal__title">Assinatura Digital</h3>
            <div className="signature-modal__header-buttons">
              <button
                onClick={() => {
                  if (signaturePadRef.current) {
                    signaturePadRef.current.clearCanvas();
                  }
                }}
                className="signature-modal__button signature-modal__button--clear"
                title="Limpar assinatura"
              >
                🗑️
              </button>
              <button
                onClick={() => {
                  setShowSignatureModal(false);
                  setIsAuthorized(false);
                }}
                className="signature-modal__button signature-modal__button--cancel"
                title="Cancelar"
              >
                ✖️
              </button>
            </div>
          </div>
          <div className="signature-modal__content">
            <SignaturePad ref={signaturePadRef} value={null} disabled={false} />
            <button
              onClick={handleConfirmSignature}
              disabled={isSaving}
              className="signature-modal__button signature-modal__button--confirm"
            >
              {isSaving ? 'Salvando...' : 'Confirmar Assinatura'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalSignature;
